export const ATTRIBUTE_TYPE_METADATA_KEY = 'attributeType';
export const SERIALIZE_METADATA_KEY = 'attributesToSerialize';
export const ANIMATABLE_METADATA_KEY = 'attributesToAnimate';
export const ALL_METADATA_KEY = 'allAttributes';
export type CommonAttributeSettings = {
    serialize: boolean;
    animatable: boolean;
};
export const DEFAULT_ATTRIBUTE_SETTINGS: CommonAttributeSettings = {
    serialize: true,
    animatable: false,
};
export type CommonMetadataKeys = keyof CommonAttributeSettings | typeof ATTRIBUTE_TYPE_METADATA_KEY;

type AssetOrBase = {
    requiresRedraw?: boolean;
    requiresUpdate?: boolean;
};

const settablePropertyCache = new WeakMap<object, Map<string, boolean>>();

function hasSettableProperty(obj: object, prop: string): boolean {
    if (!(prop in obj)) {
        return false;
    }
    const proto = Object.getPrototypeOf(obj);
    if (proto) {
        const protoCache = settablePropertyCache.get(proto);
        const cached = protoCache?.get(prop);
        if (cached !== undefined) {
            return cached;
        }
    }
    let result = false;
    let current: object | null = obj;
    while (current) {
        const desc = Object.getOwnPropertyDescriptor(current, prop);
        if (desc) {
            result = ('value' in desc && desc.writable !== false) || typeof desc.set === 'function';
            break;
        }
        current = Object.getPrototypeOf(current);
    }
    if (proto) {
        let protoCache = settablePropertyCache.get(proto);
        if (!protoCache) {
            protoCache = new Map();
            settablePropertyCache.set(proto, protoCache);
        }
        protoCache.set(prop, result);
    }
    return result;
}

export function propagateUpdate(obj: AssetOrBase) {
    if (hasSettableProperty(obj, 'requiresRedraw')) {
        obj.requiresRedraw = true;
    }
    if (hasSettableProperty(obj, 'requiresUpdate')) {
        obj.requiresUpdate = true;
    }
}

export function createAttributeSetter<T>(
    validator: (value: T) => void,
    originalGet: (this: AssetOrBase) => T,
    originalSet: (this: AssetOrBase, value: T) => void,
) {
    return function set(this: AssetOrBase, value: T) {
        const currValue = originalGet.call(this);
        if (currValue === value) {
            return;
        }
        validator.call(this, value);

        originalSet.call(this, value);
        propagateUpdate(this);
    };
}

export function defineMetadata(
    settings: Partial<CommonAttributeSettings>,
    attributeType: string,
    context: ClassAccessorDecoratorContext,
    get: (this: unknown) => unknown,
) {
    context.metadata[context.name] = {
        [ATTRIBUTE_TYPE_METADATA_KEY]: attributeType,
        ...settings,
        get,
    };

    // store some metadata on the class as well about the attribute
    if (settings.serialize) {
        const serializabkeAttributes = Object.hasOwn(context.metadata, SERIALIZE_METADATA_KEY)
            ? (context.metadata[SERIALIZE_METADATA_KEY] as (string | symbol)[])
            : [];
        serializabkeAttributes.push(context.name);
        context.metadata[SERIALIZE_METADATA_KEY] = serializabkeAttributes;
    }

    if (settings.animatable) {
        const animatableAttributes = Object.hasOwn(context.metadata, ANIMATABLE_METADATA_KEY)
            ? (context.metadata[ANIMATABLE_METADATA_KEY] as (string | symbol)[])
            : [];
        animatableAttributes.push(context.name);
        context.metadata[ANIMATABLE_METADATA_KEY] = animatableAttributes;
    }

    const allAttributes = Object.hasOwn(context.metadata, ALL_METADATA_KEY)
        ? (context.metadata[ALL_METADATA_KEY] as (string | symbol)[])
        : [];
    allAttributes.push(context.name);
    context.metadata[ALL_METADATA_KEY] = allAttributes;
}
